/**
 * App-chrome internationalization (frontend roadmap step 2).
 *
 * The app shipped English-only for ~49 rounds. This is the runtime `t()` layer plus the
 * en/da dictionaries it reads. It localizes the app's OWN chrome (nav, the sign-in screen,
 * the dashboard) - NOT the copy the app publishes to strangers, which already has its own
 * per-locale system (epkStrings, outreach/EPK locale overrides). The two never meet: one is
 * the tool's UI, the other is the artist's press material.
 *
 * Dictionaries are flat, dotted-key JSON, and the AI-fill pipeline (services/i18nTranslate.ts) can
 * diff and rewrite the files key-for-key. A brand-new key that only exists in en.json still renders
 * (English fallback), so shipping an English string ahead of its translation never shows a raw key
 * to a user.
 *
 * ⚠ **They are FETCHED now, not bundled** (2026-08-25). All three used to be `import … with { type:
 * "json" }` at the top of this file, which made `t()` a synchronous object read with nothing to
 * sequence - and made every reader download 1.2MB of dictionaries to use one of them, 45% of the
 * whole bundle. `routes/i18n.ts` serves one merged-over-English payload per locale; the trade is
 * that the dictionary has to ARRIVE before anything calls `t()`, which is why `initI18n()` returns a
 * promise and `app.ts` awaits it at the top level before it draws anything.
 *
 * ⚠ **The fetch does not start here.** `index.html`'s pre-paint script kicks it off from the same
 * cached locale it stamps the theme from, so it flies in parallel with `app.js` instead of waiting
 * for it - the same cache-then-reconcile bargain the theme and text-scale stamps make, and for the
 * same reason: a round trip that begins after a 400KB bundle has parsed is a round trip in the
 * critical path.
 *
 * Locale is per-workspace like the theme (round 49): the mover value lives under
 * workspaceSettingKey("ui_locale"), and "system" - follow the browser's language - stores NO
 * row, which is what a fresh workspace should do. Signed out (saas, no workspace yet) there is
 * nothing server-side to read, so the sign-in screen follows the localStorage cache the last
 * signed-in session wrote, else the browser.
 */
import { createStore } from "./store.ts";
import { parseStoredText, type KeyText, type StoredText } from "../../shared/types.ts";
import { isUiLocale, LOCALE_PICK_COOKIE, UI_LOCALES, type LocaleMode, type UiLocale } from "../../shared/i18n.ts";

// Re-exported so the ~30 modules that already say `import { type UiLocale } from "./i18n.ts"` keep
// working: the DECLARATION moved to shared/ (both sides need it now that serverI18n resolves the
// same set), the reader's import path did not.
export type { LocaleMode, UiLocale };

/**
 * A dictionary entry is either one string, or CLDR plural forms chosen by `Intl.PluralRules`.
 *
 * Pluralizing by gluing an "s" on (`${n} listen${n === 1 ? "" : "s"}`) is the single most common
 * way a codebase becomes untranslatable, and it is wrong in most languages: Danish inflects the
 * noun differently, Polish has three forms, Japanese has one. So a counted string ships its forms
 * and `Intl.PluralRules` - which owns CLDR's tables for every locale - picks. `other` is required
 * because every locale has it and it is the fallback when a locale's category is missing.
 */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };
export type LocaleEntry = string | PluralForms;

/**
 * The dictionaries that have ARRIVED. Empty until the first fetch resolves, which is why nothing may
 * call `t()` before `initI18n()`'s promise settles - a lookup against an absent dictionary answers
 * with the key itself, and a screen of dotted keys is the one failure mode this seam has.
 */
const DICTS: Partial<Record<UiLocale, Record<string, LocaleEntry>>> = {};

/**
 * In-flight and completed fetches, one per locale. Deduped because two things ask independently and
 * would otherwise race for the same 450KB: the boot, and `applyLocale()` reconciling with whatever
 * the workspace has stored - which on most loads is the same locale the boot already guessed.
 */
const LOADS = new Map<UiLocale, Promise<void>>();

/**
 * The head start `index.html` gives us: the pre-paint script has already asked for a dictionary and
 * parked the promise here. ⚠ It resolves to `null` rather than rejecting when the request fails, so
 * a failed head start falls through to a real fetch below instead of taking the app down with it.
 */
declare global {
	interface Window {
		__ghDictionary?: { locale: string; ready: Promise<Record<string, LocaleEntry> | null> };
	}
}

/**
 * Fetch one locale's dictionary, once.
 *
 * ⚠ **It retries, and then it gives up quietly.** Every string in the app comes through here, so a
 * transient failure would leave a reader looking at `nav.dashboard` where a word should be. One
 * retry covers the case that actually happens (a sleeping laptop's first request after waking);
 * beyond that the app renders keys, which is ugly and honest, rather than blocking forever on a
 * spinner that has nothing behind it.
 */
async function fetchDictionary(locale: UiLocale): Promise<void> {
	const head = window.__ghDictionary;
	if (head?.locale === locale) {
		const dict = await head.ready;
		if (dict != null) {
			DICTS[locale] = dict;
			return;
		}
	}
	for (let attempt = 0; attempt < 2; attempt++) {
		try {
			const res = await fetch(`/i18n/${locale}`, { headers: { Accept: "application/json" } });
			if (res.ok) {
				DICTS[locale] = (await res.json()) as Record<string, LocaleEntry>;
				return;
			}
		} catch {
			// fall through to the retry, then to the honest failure above
		}
	}
}

/**
 * Install a dictionary directly, with no fetch.
 *
 * ⚠ **This exists for the SUITE and has no caller in the app.** A handful of unit tests assert that
 * a `KeyText` payload the server built says the right sentence in the reader's language, and they
 * run in node with no `fetch`, no `document` and no boot - `tests/dictionary.ts` seeds them through
 * here. It is exported rather than reached into because the alternative was for those tests to keep
 * working by accident: while the dictionaries were bundled they simply WERE there, which is exactly
 * the coupling that made this file 45% of the app's download.
 */
export function installDictionary(locale: UiLocale, dict: Record<string, LocaleEntry>): void {
	DICTS[locale] = dict;
	LOADS.set(locale, Promise.resolve());
}

function loadDictionary(locale: UiLocale): Promise<void> {
	let load = LOADS.get(locale);
	if (!load) {
		load = fetchDictionary(locale);
		LOADS.set(locale, load);
	}
	return load;
}

/** Intl objects are expensive to build and cheap to keep - one per locale, made on first use. */
const PLURAL_RULES = new Map<UiLocale, Intl.PluralRules>();
const NUMBER_FORMATS = new Map<UiLocale, Intl.NumberFormat>();
const COMPACT_FORMATS = new Map<UiLocale, Intl.NumberFormat>();
const PERCENT_FORMATS = new Map<UiLocale, Intl.NumberFormat>();
const RATE_FORMATS = new Map<UiLocale, Intl.NumberFormat>();
const RELATIVE_FORMATS = new Map<UiLocale, Intl.RelativeTimeFormat>();
const DATE_TIME_FORMATS = new Map<UiLocale, Intl.DateTimeFormat>();
const DATE_FORMATS = new Map<UiLocale, Intl.DateTimeFormat>();
const DAY_FORMATS = new Map<UiLocale, Intl.DateTimeFormat>();
const MONTH_FORMATS = new Map<UiLocale, Intl.DateTimeFormat>();
const CURRENCY_FORMATS = new Map<string, Intl.NumberFormat>();

function pluralRules(locale: UiLocale): Intl.PluralRules {
	let rules = PLURAL_RULES.get(locale);
	if (!rules) {
		rules = new Intl.PluralRules(locale);
		PLURAL_RULES.set(locale, rules);
	}
	return rules;
}
/** Caches the last resolved locale so the next boot can localize chrome before the server answers */
const LOCALE_CACHE_KEY = "ui-locale";

/**
 * The active locale as a store (frontend/store.ts), because it is the canonical case of
 * state two worlds must react to: applyStaticI18n rewrites the data-i18n chrome, but a
 * Lit island renders t() calls at render time, which no attribute sweep can reach -
 * islands subscribe and re-render instead. Everything in this module reads the store,
 * so t() and the Intl helpers can never disagree with what subscribers were told.
 */
export const localeStore = createStore<UiLocale>("en");
const current = (): UiLocale => localeStore.get();

/**
 * The language this browser's reader PICKED, from `LOCALE_PICK_COOKIE`, or null. Pure over the cookie
 * string so a test can hand it one.
 */
export function pickedLocaleFrom(cookie: string): UiLocale | null {
	for (const part of cookie.split(";")) {
		const eq = part.indexOf("=");
		if (eq !== -1 && part.slice(0, eq).trim() === LOCALE_PICK_COOKIE) {
			const value = part.slice(eq + 1).trim();
			return isUiLocale(value) ? value : null;
		}
	}
	return null;
}

function pickedLocale(): UiLocale | null {
	return typeof document !== "undefined" ? pickedLocaleFrom(document.cookie) : null;
}

/**
 * Resolve a stored mode to a concrete locale. Pure, and exported for the test.
 *
 * ⚠ **"system" means "no ACCOUNT choice", and it asks this browser's PICK before its language**
 * (2026-09-25). A reader whose phone is Danish and who chose English on the front door pressed
 * "Log ind", "Kom i gang" or "Se et eksempel" and landed in Danish: the pick was a cookie the app never
 * read. None of those three places has an account language to follow - a signed-out screen, a fresh
 * guest workspace, the shared example - so they all resolve "system", and the pick is the closer answer
 * to "what does this reader want" than a header the phone sends on its own. An account that stored a
 * language still wins, because `isUiLocale(mode)` answers first.
 */
export function resolveLocaleMode(mode: LocaleMode, picked: UiLocale | null, browserTag: string): UiLocale {
	if (isUiLocale(mode)) return mode;
	if (picked != null) return picked;
	// A browser sends a full tag ("de-AT", "da-DK"); we ship dictionaries per LANGUAGE, so the
	// region is dropped. `isUiLocale` narrows, so the old `as UiLocale` cast is gone - a cast at
	// this boundary launders exactly the mistake the union exists to catch (round 179).
	const short = browserTag.slice(0, 2).toLowerCase();
	return isUiLocale(short) ? short : "en";
}

function resolve(mode: LocaleMode): UiLocale {
	return resolveLocaleMode(mode, pickedLocale(), (typeof navigator !== "undefined" ? navigator.language : "") || "");
}

/**
 * Keep the browser's pick in step with a language chosen IN the app (Settings, or its reset): pin it,
 * or clear it for "system". ⚠ **Only a reader's own choice calls this, never `applyLocale`**: the boot
 * reconcile applies the workspace's stored mode, which is "system" for every fresh guest, and clearing
 * the pick there would throw away the very choice the front door just made.
 */
export function rememberLocalePick(mode: LocaleMode): void {
	try {
		const secure = location.protocol === "https:" ? "; Secure" : "";
		document.cookie = isUiLocale(mode)
			? `${LOCALE_PICK_COOKIE}=${mode}; Path=/; SameSite=Lax; Max-Age=31536000${secure}`
			: `${LOCALE_PICK_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0${secure}`;
	} catch {}
}

export function currentLocale(): UiLocale {
	return current();
}

/**
 * Substitute `{name}`-style placeholders. Kept pure and exported so the contract (which
 * placeholders a string carries) is unit-testable without a DOM - the dictionary test asserts
 * en and da agree on exactly this set per key.
 */
export function interpolate(raw: string, params?: Record<string, string | number>): string {
	if (!params) return raw;
	return raw.replace(/\{(\w+)\}/g, (whole, key: string) => (key in params ? String(params[key]) : whole));
}

/**
 * Pick a plural form for `count` in a locale. Exported so the dictionary test can assert a
 * locale's forms cover the categories `Intl.PluralRules` will actually ask it for.
 */
export function selectPluralForm(forms: PluralForms, count: number, locale: UiLocale = current()): string {
	return forms[pluralRules(locale).select(count)] ?? forms.other;
}

/** Numbers in the reader's locale: 1234 is "1,234" in English and "1.234" in Danish. */
export function formatNumber(value: number, locale: UiLocale = current()): string {
	let fmt = NUMBER_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale);
		NUMBER_FORMATS.set(locale, fmt);
	}
	return fmt.format(value);
}

/**
 * Minor units (cents/øre) → a localized currency string: 1800 USD → "$18.00" (en) / "18,00 US$" (da).
 * `Intl.NumberFormat` owns the symbol, its placement and the decimal separator - none of which are
 * ours to translate. Every currency the ROI board offers is two-decimal (see PROMO_CURRENCIES), so
 * `cents / 100` is always the right major-unit conversion.
 */
export function formatMoney(cents: number, currency: string, locale: UiLocale = current()): string {
	const key = `${locale}:${currency}`;
	let fmt = CURRENCY_FORMATS.get(key);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
		CURRENCY_FORMATS.set(key, fmt);
	}
	return fmt.format(cents / 100);
}

/**
 * A ratio → a signed percentage in the reader's locale: 0.33 → "+33%" (en) / "+33 %" (da).
 *
 * ⚠ Danish puts a space before the percent sign and English does not, which is exactly why this
 * belongs to `Intl` and not to a `${Math.round(x * 100)}%` at a call site. That spelling has no
 * locale at all - round 143's finding was that a hardcoded locale ARGUMENT is as much an English
 * bug as a hardcoded string, and a formatter with no locale is the same bug with the argument left
 * out. It is named for a CHANGE rather than a percentage because `signDisplay` is baked in: the
 * three older `${Math.round(…)}%` sites in this app render RATES, where a leading "+" would be
 * wrong, so they use the sibling below, `formatPercent`.
 */
export function formatPercentChange(ratio: number, locale: UiLocale = current()): string {
	let fmt = PERCENT_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0, signDisplay: "exceptZero" });
		PERCENT_FORMATS.set(locale, fmt);
	}
	return fmt.format(ratio);
}

/**
 * A ratio → a RATE in the reader's locale, unsigned: 0.05 → "5%" (en) / "5 %" (da). The sibling the
 * note above asked for (round 324): the funnel, the channel table and the angle chips each spelled
 * their own, with no locale at all.
 */
const FINE_RATE_FORMATS = new Map<string, Intl.NumberFormat>();
/**
 * A SMALL rate with one decimal ("4.8%") - likes per view live between 0.5% and 5%, where whole
 * percents turn 0.7%, 1.9% and 4.8% into 1%, 2% and 5% and hide the very difference being shown.
 */
export function formatFinePercent(ratio: number, locale: UiLocale = current()): string {
	let fmt = FINE_RATE_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 1 });
		FINE_RATE_FORMATS.set(locale, fmt);
	}
	return fmt.format(ratio);
}

export function formatPercent(ratio: number, locale: UiLocale = current()): string {
	let fmt = RATE_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 });
		RATE_FORMATS.set(locale, fmt);
	}
	return fmt.format(ratio);
}

/** 1284 → "1.3K" (en) / "1,3 tsd." (da). Intl owns the unit words, which are not translatable by us. */
export function formatCompact(value: number, locale: UiLocale = current()): string {
	let fmt = COMPACT_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 });
		COMPACT_FORMATS.set(locale, fmt);
	}
	return fmt.format(value);
}

/**
 * A stored timestamp as the reader's own date - "Jul 26, 2026" in English, "26. jul. 2026" in Danish.
 *
 * This printed bare `YYYY-MM-DD` in every locale until round 123, which had made it the odd one out
 * in its own module: the app formats numbers through `NumberFormat`, elapsed time through
 * `RelativeTimeFormat`, and since round 122 country and language names through `DisplayNames` - and
 * then dated everything in an ISO string nobody speaks. A date's FORMAT is per-locale in exactly the
 * way its month name is (Americans put the month first, most of Europe does not), so it is the same
 * rule as the rest, and the reason it lagged is that ISO looks deliberate rather than untranslated.
 *
 * `dateStyle: "medium"`, not `"short"`: a numeric-only date is genuinely ambiguous across locales
 * (07/26 vs 26/07) and these sit in table cells and hints where the reader is scanning, so the
 * abbreviated month buys unambiguity for two characters.
 *
 * ⚠ Display only. Anything that must round-trip - an `<input type="date">` value, a manifest join
 * key, a `?src=` tag, a stored column - keeps its ISO string and does not come through here.
 */
export function formatDate(dtStr: string | null, locale: UiLocale = current()): string {
	const parsed = parseStamp(dtStr);
	if (!parsed) return dtStr ?? "";
	let fmt = DATE_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
		DATE_FORMATS.set(locale, fmt);
	}
	return fmt.format(parsed);
}

/**
 * A day WITHOUT its year - "Jul 14" in English, "14. jul." in Danish - for a chart axis or a
 * tooltip, where the year is already implied by the range being drawn.
 *
 * ⚠ It exists because `<gh-area-chart>` had `toLocaleDateString("en-US", …)` hardcoded, which is
 * round 122's `Intl.DisplayNames(["en"])` bug in a different component: the RIGHT mechanism with a
 * pinned locale, so nothing at the call site looks wrong and every chart in a Danish app reads its
 * axis in English. Found by round 143's verify run on a fully translated page - the one English
 * words left on it were on a chart nobody had reason to re-read.
 *
 * `formatDate` is deliberately not reused: it is `dateStyle: "medium"`, so an axis of thirty points
 * would carry the year thirty times.
 */
export function formatDayShort(dtStr: string, locale: UiLocale = current()): string {
	const parsed = parseStamp(dtStr);
	if (!parsed) return dtStr;
	let fmt = DAY_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
		DAY_FORMATS.set(locale, fmt);
	}
	return fmt.format(parsed);
}

/**
 * A month and its year - "September 2026" - for a list's group heading (§AL: the Sent log is grouped
 * by month while it is sorted by a date). The browser's zone, like `formatDate` on the rows beneath it,
 * so a pitch sent late on the 30th sits under the month its own date says.
 */
export function formatMonthYear(dtStr: string | null, locale: UiLocale = current()): string {
	const parsed = parseStamp(dtStr);
	if (!parsed) return "";
	let fmt = MONTH_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
		MONTH_FORMATS.set(locale, fmt);
	}
	return fmt.format(parsed);
}

/**
 * A day spelled out in full - "Wednesday, September 23" - for a page heading (§AL: Today's heading is
 * the date). No year: it is always this year's today. Read as UTC because the input is a bare
 * `YYYY-MM-DD` the server already resolved on the WORKSPACE's clock; a local-zone read would move it
 * a day for anyone west of UTC.
 */
export function formatDayLong(day: string, locale: UiLocale = current()): string {
	const parsed = new Date(`${day}T12:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return day;
	return new Intl.DateTimeFormat(locale, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }).format(parsed);
}

/**
 * A date RANGE, formatted by Intl rather than glued together - "Jul 18 – 25, 2026", where the shared
 * month and year appear once and the separator is the locale's own.
 *
 * `formatRange` exists precisely because `${a} → ${b}` is the date equivalent of concatenating a
 * sentence: which part collapses, in which order, with which dash, is per-locale. The Ads panel built
 * that arrow by hand, which is what the trap looks like from the inside - two correctly formatted
 * dates with a hardcoded glyph between them looks finished.
 */
export function formatDateRange(fromStr: string | null, toStr: string | null, locale: UiLocale = current()): string {
	const from = parseStamp(fromStr);
	const to = parseStamp(toStr);
	if (!from || !to) return formatDate(from ? fromStr : toStr, locale);
	let fmt = DATE_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
		DATE_FORMATS.set(locale, fmt);
	}
	return fmt.formatRange(from, to);
}

/** A stamp that already declares its zone: a trailing `Z`, or a `+02:00`/`+0200` offset */
const STAMP_HAS_ZONE = /[zZ]$|[+-]\d\d:?\d\d$/;
/** A stamp with a clock in it, as opposed to a bare "2026-07-26" that `Date` already reads as UTC */
const STAMP_HAS_TIME = /\d{2}:\d{2}/;

/**
 * One answer to "turn a stored stamp into a Date", for every formatter in this file.
 *
 * SQLite writes "2026-07-26 09:00:00": `Date` wants the T, and the value is **UTC with no marker
 * saying so**, which is the part this used to get wrong. An unmarked stamp handed to `Date` is read
 * as the VIEWER's local time, so every time in the app was displayed off by the viewer's own offset
 * - two hours in Copenhagen, silently, and in the direction that still looks like a plausible time.
 *
 * ⚠ **THE finding, and it is the workspace-clock lane's own rule arriving in the frontend: this
 * question had THREE answers in one file family and they disagreed** (2026-08-21, from an owner
 * report of a raw `2026-07-15T08:45:38+0000` on the Content page's post dialog). `relativeTime`
 * pinned unmarked stamps to Z and tested for an existing zone first, which is correct;
 * `parseStamp` did neither; and `postDetail.ts`'s own `#when` tested only for `Z` and so appended a
 * SECOND zone to Instagram's `+0000`, producing `...+0000Z`, an invalid date, and a fallback that
 * printed the raw string. **Each was locally reasonable, which is why reviewing any one of them
 * alone could never find it.** All three go through here now.
 *
 * ⚠ **The date-only case is why the time check exists.** `new Date("2026-07-15")` is UTC midnight by
 * specification, and a release date arrives exactly like that - so appending `Z` to it would produce
 * `"2026-07-15Z"`, which is invalid, and break every `formatDate` call in the app to fix a bug in
 * `formatDateTime`. **A stamp is pinned only when it carries a clock and no zone.**
 */
export function parseStamp(dtStr: string | null | undefined): Date | null {
	if (!dtStr) return null;
	const iso = dtStr.replace(/ /g, "T");
	const pin = STAMP_HAS_TIME.test(iso) && !STAMP_HAS_ZONE.test(iso);
	const parsed = new Date(pin ? `${iso}Z` : iso);
	return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A stamp with its CLOCK TIME, for the tooltip behind a relative one.
 *
 * ⚠ **A relative time is a comfort, not a record**, and a support thread is where that matters:
 * "5 minutes ago" is what a reader wants at a glance and "14:32" is what they need when they are
 * telling somebody else when it happened. So the two travel together - the relative one on screen,
 * this one on hover - rather than one replacing the other.
 *
 * ⚠ `dateStyle`/`timeStyle` rather than a hand-built pattern: 12- versus 24-hour, the separator and
 * the field order are all the LOCALE's to decide, and a hardcoded locale argument is as much a
 * hardcoded-English bug as a hardcoded string (CLAUDE.md's rule for this seam).
 */
export function formatDateTime(dtStr: string | null, locale: UiLocale = current()): string {
	const parsed = parseStamp(dtStr);
	if (!parsed) return dtStr ?? "";
	let fmt = DATE_TIME_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });
		DATE_TIME_FORMATS.set(locale, fmt);
	}
	return fmt.format(parsed);
}

/**
 * "3 minutes ago" / "in 2 days" / "yesterday", in the active locale.
 *
 * `Intl.RelativeTimeFormat` picks the unit's plural form AND the past/future wording itself, which
 * is exactly the work a hand-rolled version cannot do outside English - the one this replaced built
 * "1 hour"/"2 hours" by gluing on an "s". `numeric: "auto"` is what turns -1 day into "yesterday"
 * rather than "1 day ago". The same Intl the reel countdown chip already runs on (shared/reelChip).
 *
 * SQL timestamps arrive as "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker, so an unmarked string
 * is pinned to Z - read as local time it would drift by the viewer's offset. ⚠ That reasoning was
 * RIGHT and it was spelled out only here, inline, where the two formatters above could not reach it;
 * it lives in `parseStamp` now and this function asks that instead of carrying its own copy.
 */
export function relativeTime(when: Date | string, now: Date = new Date(), locale: UiLocale = current()): string {
	const parsed = typeof when === "string" ? parseStamp(when) : when;
	if (!parsed || isNaN(parsed.getTime())) return typeof when === "string" ? when : "";
	let fmt = RELATIVE_FORMATS.get(locale);
	if (!fmt) {
		fmt = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
		RELATIVE_FORMATS.set(locale, fmt);
	}
	const seconds = Math.round((parsed.getTime() - now.getTime()) / 1000);
	const abs = Math.abs(seconds);
	const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
		abs < 45
			? [0, "second"]
			: abs < 3600
				? [Math.round(seconds / 60), "minute"]
				: abs < 86_400
					? [Math.round(seconds / 3600), "hour"]
					: abs < 604_800
						? [Math.round(seconds / 86_400), "day"]
						: abs < 2_592_000
							? [Math.round(seconds / 604_800), "week"]
							: abs < 31_536_000
								? [Math.round(seconds / 2_592_000), "month"]
								: [Math.round(seconds / 31_536_000), "year"];
	return fmt.format(value, unit);
}

/**
 * Translate a key in the active locale. Missing in the active locale → English; missing in
 * English too → the key itself (so a typo is loud in dev, never a blank in prod).
 *
 * A `count` param does double duty: `Intl.PluralRules` selects the form with it, and it renders
 * through `Intl.NumberFormat` so a four-figure count is grouped the way the reader's locale groups
 * numbers. Only `count` is auto-formatted - a `{year}` param must never come out as "2,026".
 */
export function t(key: string, params?: Record<string, string | number>): string {
	const entry = DICTS[current()]?.[key] ?? DICTS.en?.[key] ?? key;
	const count = typeof params?.count === "number" ? params.count : null;
	const raw = typeof entry === "string" ? entry : selectPluralForm(entry, count ?? 0);
	return interpolate(raw, count === null ? params : { ...params, count: formatNumber(count) });
}

/**
 * Join a list of phrases the way the reader's locale joins one: "a, b, c" in English, "a, b og c" in
 * Danish, "a、b、c" in Japanese.
 *
 * The fifth member of "let Intl do what Intl owns" (round 125), and it is the LIST equivalent of the
 * rule against concatenating a sentence - `parts.join(", ")` bakes an English separator into every
 * locale exactly as `"About " + name` bakes in English word order. Danish is the worked example that
 * makes the point: it wants a real "og" before the last item where English is content with a comma,
 * and no `join()` argument can know that.
 *
 * `type: "unit"` is the DEFAULT because most lists here are enumerations of evidence rather than prose
 * alternatives ("covered 1 release, replied 2×"). ⚠ **A list read as part of a SENTENCE takes
 * `"conjunction"`**, and the difference is visible in the one language this app is certainly read in
 * besides English: "the Spotify link, the lyrics" is a caption, "the Spotify link and the lyrics" is
 * something a person said. Passing the type is the whole of it - what must never happen is a caller
 * hand-joining with " and ", which is the English word order this helper exists to keep out.
 */
const LIST_FORMATS = new Map<string, Intl.ListFormat>();
export function formatList(parts: string[], locale: UiLocale = current(), type: "unit" | "conjunction" | "disjunction" = "unit"): string {
	// ⚠ Keyed by locale AND type: one cache per locale silently served the first type anybody asked
	// for to every later caller, which is a formatter quietly answering a question it was not asked.
	const cacheKey = `${locale}:${type}`;
	let fmt = LIST_FORMATS.get(cacheKey);
	if (!fmt) {
		fmt = new Intl.ListFormat(locale, { style: "long", type });
		LIST_FORMATS.set(cacheKey, fmt);
	}
	return fmt.format(parts);
}

/**
 * Say a sentence the SERVER decided (the round-70 key seam's payload) in the reader's language.
 *
 * This is the one place the seam is resolved, because four of its rules are the reader's alone and
 * every call site would otherwise have to remember them: a `date` param arrives ISO and only this
 * side knows how the locale writes it; ⚠ **a NUMBER is the same problem and went unnoticed until
 * round 151** - `1.5` is "1,5" in Danish and `interpolate` only ever did `String(value)`, so every
 * measured quantity the seam carried read in English arithmetic (round 143's lesson: *only reading
 * the app in the other language finds this*); a `parts` entry is a decided NOUN PHRASE that has to
 * be said before the frame can interpolate it (round 124 - see `KeyText.parts` for why a phrase may
 * nest and a clause may not); and a missing key renders as the key itself, loudly, rather than as a
 * blank. `#apText` in the Runway island was the first hand-rolled half of this and now delegates.
 *
 * ⚠ **`count` is deliberately left a NUMBER.** `t()` owns it - it picks the CLDR plural form from
 * the raw value and formats it afterwards - so pre-formatting here would hand `t()` a string, its
 * `typeof params.count === "number"` guard would fail, and every plural entry would silently answer
 * with the `other` form. A conversion that looks like a tidy-up and breaks grammar for count = 1.
 */
export function tKey(text: KeyText): string {
	const params: Record<string, string | number> = { ...text.params };
	if (text.params?.date != null) params.date = formatDate(String(text.params.date));
	// ⚠ **MONEY is the `date` rule's fifth sibling** (2026-08-26): a currency is not a number with a
	// symbol in front of it - the symbol's SIDE, the decimal separator and the space all move with the
	// reader's locale, and `Intl` owns every one of them. The server sends `amountCents` + `currency`
	// and never a formatted string, because formatting on that side would pick the SERVER's locale for
	// a sentence somebody else is about to read: round 143's finding that a hardcoded locale argument
	// is as much an English bug as a hardcoded string. The pair is consumed into one `{amount}` so a
	// dictionary entry never has to place a currency itself.
	if (typeof text.params?.amountCents === "number" && text.params.currency != null) {
		params.amount = formatMoney(text.params.amountCents, String(text.params.currency));
		delete params.amountCents;
		delete params.currency;
	}
	for (const [name, value] of Object.entries(params)) {
		if (name !== "count" && name !== "date" && name !== "amount" && typeof value === "number") params[name] = formatNumber(value);
	}
	for (const [name, part] of Object.entries(text.parts ?? {})) params[name] = tKey(part);
	return t(text.key, params);
}

/**
 * A sentence read out of a column, which is a key when the app wrote it after that column joined the
 * seam and the English it composed at the time when it did not (`StoredText`). Old rows are rendered
 * verbatim rather than migrated - see the type.
 */
export function tStored(stored: StoredText): string {
	return "key" in stored ? tKey(stored) : stored.text;
}

/**
 * The same thing starting from the raw COLUMN (round 152), returning `null` when there is nothing
 * recorded - which is a state every reader of these fields has to render differently ("failed" with
 * no reason is not "failed: ").
 *
 * It exists so no call site does `parseStoredText` itself: forgetting that step is invisible in the
 * common case, because a pre-seam row and a foreign diagnostic are both plain text that renders
 * correctly by accident, and only OUR rows come out as raw JSON.
 */
export function tColumn(stored: string | null | undefined): string | null {
	const parsed = parseStoredText(stored);
	return parsed == null ? null : tStored(parsed);
}

/**
 * Rewrite the static chrome baked into index.html. `data-i18n` sets textContent (nav links);
 * `data-i18n-title`/`data-i18n-placeholder` localize those attributes. Idempotent, so it can
 * run on every locale change. Elements the slice hasn't reached simply have no data-i18n and
 * keep their authored English - the migration wraps pages one at a time, not wholesale.
 */
export function applyStaticI18n(root: ParentNode = document): void {
	root.querySelectorAll<HTMLElement>("[data-i18n]").forEach(el => {
		const key = el.dataset.i18n;
		if (key) el.textContent = t(key);
	});
	root.querySelectorAll<HTMLElement>("[data-i18n-title]").forEach(el => {
		const key = el.dataset.i18nTitle;
		if (key) el.title = t(key);
	});
	root.querySelectorAll<HTMLInputElement>("[data-i18n-placeholder]").forEach(el => {
		const key = el.dataset.i18nPlaceholder;
		if (key) el.placeholder = t(key);
	});
	// §R: an accessible NAME is user-facing text and has to travel like the rest of it. It arrived
	// with the sidebar, which needs a name because the app has two <nav> landmarks on several pages
	// and a screen reader offering "navigation, navigation" is offering a coin flip.
	root.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach(el => {
		const key = el.dataset.i18nAria;
		if (key) el.setAttribute("aria-label", t(key));
	});
}

/**
 * Set the active locale from a stored mode, cache it, stamp <html lang>, refresh static chrome.
 *
 * ⚠ **Async since the dictionaries left the bundle**, and the ORDER inside is the point: the store is
 * not moved until the new dictionary is here. Setting it first would notify every island to
 * re-render against a dictionary that does not exist yet, and the whole app would flash a screen of
 * dotted keys on its way to Danish.
 */
export async function applyLocale(mode: LocaleMode): Promise<void> {
	await loadDictionary(resolve(mode));
	// The store notifies its subscribers (Lit islands re-render themselves); the
	// data-i18n chrome below is the pre-store world and still needs the explicit sweep
	localeStore.set(resolve(mode));
	try {
		localStorage.setItem(LOCALE_CACHE_KEY, current());
	} catch {}
	document.documentElement.lang = current();
	applyStaticI18n();
}

/**
 * The boot step: pick the locale from the reader's pick, else the cache, else the browser, get its dictionary, and localize
 * the static chrome before the first server round-trip. initTheme() then reconciles with the
 * workspace's stored mode and re-applies if it differs - the same cache-then-reconcile shape the
 * theme pre-paint uses, so a returning Danish user never sees English flash in.
 *
 * ⚠ **Awaited at the top level of `app.ts`, and everything after it depends on that.** It stopped
 * being synchronous when the dictionaries became a fetch; the guess it makes here is the same one
 * `index.html` made a few hundred milliseconds earlier, so the await is usually already settled.
 */
export async function initI18n(): Promise<void> {
	let cached: string | null = null;
	try {
		cached = localStorage.getItem(LOCALE_CACHE_KEY);
	} catch {}
	// The pick beats the cache: the cache is what the LAST signed-in session here resolved to, and a
	// reader who has since chosen a language on the front door has told us something newer.
	const locale = pickedLocale() ?? (isUiLocale(cached) ? cached : resolve("system"));
	await loadDictionary(locale);
	localeStore.set(locale);
	document.documentElement.lang = current();
	applyStaticI18n();
}
